import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperKbdComponent, PaperTagComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import type { CmdkSpaceItem } from '../models/cmdk.model';

/** One label/value line in the details block. */
interface SpaceFact {
  readonly label: string;
  readonly value: string;
}

/**
 * The preview for a space.
 *
 * AI CONTEXT — why there is no photo, no floor plan and no timeline
 *
 * The prototype's space preview leads with a room photo, an availability strip
 * for today and a map pin. None of the three is reachable from what the search
 * returns: `searchMap` gives name, type, floor, capacity, amenities and a
 * single boolean for "free at the requested time". There is no image field, no
 * per-slot schedule and — on the estate-wide path — not even a locationId to
 * anchor a map to.
 *
 * Rendering a grey placeholder where the photo goes would make every space
 * look like it was missing an image, and synthesising a timeline from one
 * boolean would be inventing data. The pane shows what is known and hands off
 * to the space detail panel, which does have the whole record.
 *
 * `availability: 'unknown'` gets a sentence rather than a chip, because the
 * honest statement is about THIS SURFACE not knowing, not about the room.
 */
@Component({
  selector: 'im-cmdk-preview-space',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperKbdComponent, PaperTagComponent],
  templateUrl: './cmdk-preview-space.component.html',
  styleUrl: './cmdk-preview-space.component.scss',
})
export class CmdkPreviewSpaceComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkSpaceItem>();
  readonly activated = output<void>();

  protected readonly tagIntent = computed(() => {
    switch (this.item().availability) {
      case 'available': return 'emerald' as const;
      case 'busy': return 'rose' as const;
      default: return 'amber' as const;
    }
  });

  protected readonly tagLabel = computed(() => {
    this.locale.locale();
    const availability = this.item().availability;
    if (availability === 'unknown') return null;
    return this.transloco.translate(`shell.cmdk.availability.${availability}`);
  });

  protected readonly unknownNote = computed(() => {
    this.locale.locale();
    return this.item().availability === 'unknown'
      ? this.transloco.translate('shell.cmdk.preview_space.unknown_availability')
      : null;
  });

  /** Only the facts we actually have — a blank row is worse than a short list. */
  protected readonly facts = computed<SpaceFact[]>(() => {
    this.locale.locale();
    const item = this.item();
    const t = (key: string): string => this.transloco.translate(`shell.cmdk.preview_space.${key}`);
    const out: SpaceFact[] = [{ label: t('type'), value: item.spaceType }];
    if (item.floorName) out.push({ label: t('floor'), value: item.floorName });
    if (item.locationName) out.push({ label: t('building'), value: item.locationName });
    if (item.capacity) {
      out.push({
        label: t('capacity'),
        value: this.transloco.translate(
          item.capacity === 1 ? 'shell.cmdk.capacity.one' : 'shell.cmdk.capacity.other',
          { count: item.capacity },
        ),
      });
    }
    return out;
  });

  protected readonly amenities = computed(() => this.item().amenities ?? []);

  protected readonly detailsLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_space.details');
  });

  protected readonly amenitiesLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_space.amenities');
  });

  protected readonly openLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_space.open');
  });
}
